<template>
  <!-- Main Header -->
  <component :is="navbarFixed ? 'a-affix' : 'div'" :offset-top="top">
    <!-- Layout Header -->
    <a-layout-header>
      <a-row type="flex">

        <!-- Header Breadcrumbs & Title Column -->
        <a-col :span="24" :md="6">
          <!-- Header Breadcrumbs -->
          <a-breadcrumb>
            <a-breadcrumb-item>
              <router-link to="/">Pages</router-link>
            </a-breadcrumb-item>
            <a-breadcrumb-item>{{ currentRouteName }}</a-breadcrumb-item>
          </a-breadcrumb>
          <!-- / Header Breadcrumbs -->

          <!-- Header Page Title -->
          <div class="ant-page-header-heading">
            <span class="ant-page-header-heading-title">{{ currentRouteName }}</span>
          </div>
          <!-- / Header Page Title -->
        </a-col>
        <!-- / Header Breadcrumbs & Title Column -->

        <!-- Header Control Column -->
        <a-col :span="24" :md="18" class="header-control">

          <!-- Header Control Buttons -->
          <a-dropdown :trigger="['click']" overlay-class-name="header-notifications-dropdown" :get-popup-container="getContainer" v-model:open="notificationDropdownOpen">
            <a-badge :count="unreadCount" :overflow-count="99">
              <a class="ant-dropdown-link notification-bell" @click.prevent>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 2C6.68632 2 4.00003 4.68629 4.00003 8V11.5858L3.29292 12.2929C3.00692 12.5789 2.92137 13.009 3.07615 13.3827C3.23093 13.7564 3.59557 14 4.00003 14H16C16.4045 14 16.7691 13.7564 16.9239 13.3827C17.0787 13.009 16.9931 12.5789 16.7071 12.2929L16 11.5858V8C16 4.68629 13.3137 2 10 2Z" fill="#111827"/>
                  <path d="M10 18C8.34315 18 7 16.6569 7 15H13C13 16.6569 11.6569 18 10 18Z" fill="#111827"/>
                </svg>
              </a>
            </a-badge>
            
            <template #overlay>
              <div class="notifications-dropdown-content">
                <div class="notifications-header">
                  <span class="notifications-title">Notifications</span>
                  <a-button v-if="unreadCount > 0" type="link" size="small" @click="markAllAsRead" :loading="markingAllRead">
                    Mark all as read
                  </a-button>
                </div>
                
                <div v-if="loadingNotifications" class="notifications-loading">
                  <a-spin size="small" />
                  <span>Loading...</span>
                </div>
                
                <div v-else-if="notifications.length === 0" class="notifications-empty">
                  <BellOutlined style="font-size: 32px; color: #d1d5db;" />
                  <span>No notifications yet</span>
                </div>
                
                <div v-else class="notifications-list">
                  <div 
                    v-for="item in notifications" 
                    :key="item.id" 
                    class="notification-item"
                    :class="{ 'unread': !item.is_read }"
                    @click="handleNotificationClick(item)"
                  >
                    <div class="notification-icon" :class="item.type">
                      <InfoCircleOutlined v-if="item.type === 'info'" />
                      <WarningOutlined v-else-if="item.type === 'warning'" />
                      <CloseCircleOutlined v-else-if="item.type === 'error'" />
                      <CheckCircleOutlined v-else-if="item.type === 'success'" />
                      <BellOutlined v-else />
                    </div>
                    <div class="notification-content">
                      <div class="notification-title">{{ item.title }}</div>
                      <div class="notification-message">{{ item.message }}</div>
                      <div class="notification-time">{{ formatTime(item.created_at) }}</div>
                    </div>
                    <div v-if="!item.is_read" class="notification-dot"></div>
                  </div>
                </div>
                
                <div class="notifications-footer">
                  <a-button type="link" block @click="goToNotifications">
                    View all notifications
                  </a-button>
                </div>
              </div>
            </template>
          </a-dropdown>

          <a-button type="link" ref="secondarySidebarTriggerBtn" @click="$emit('toggleSettingsDrawer', true)">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M11.4892 3.17094C11.1102 1.60969 8.8898 1.60969 8.51078 3.17094C8.26594 4.17949 7.11045 4.65811 6.22416 4.11809C4.85218 3.28212 3.28212 4.85218 4.11809 6.22416C4.65811 7.11045 4.17949 8.26593 3.17094 8.51078C1.60969 8.8898 1.60969 11.1102 3.17094 11.4892C4.17949 11.7341 4.65811 12.8896 4.11809 13.7758C3.28212 15.1478 4.85218 16.7179 6.22417 15.8819C7.11045 15.3419 8.26594 15.8205 8.51078 16.8291C8.8898 18.3903 11.1102 18.3903 11.4892 16.8291C11.7341 15.8205 12.8896 15.3419 13.7758 15.8819C15.1478 16.7179 16.7179 15.1478 15.8819 13.7758C15.3419 12.8896 15.8205 11.7341 16.8291 11.4892C18.3903 11.1102 18.3903 8.8898 16.8291 8.51078C15.8205 8.26593 15.3419 7.11045 15.8819 6.22416C16.7179 4.85218 15.1478 3.28212 13.7758 4.11809C12.8896 4.65811 11.7341 4.17949 11.4892 3.17094ZM10 13C11.6569 13 13 11.6569 13 10C13 8.34315 11.6569 7 10 7C8.34315 7 7 8.34315 7 10C7 11.6569 8.34315 13 10 13Z" fill="#111827"/>
            </svg>
          </a-button>

          <a-button type="link" class="sidebar-toggler" @click="handleToggleSidebar">
            <svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
              <path d="M16 132h416c8.837 0 16-7.163 16-16V76c0-8.837-7.163-16-16-16H16C7.163 60 0 67.163 0 76v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16z"/>
            </svg>
          </a-button>

          <!-- User Profile Dropdown (when logged in) -->
          <a-dropdown v-if="isAuthenticated" :trigger="['click']" placement="bottomRight">
            <a class="user-dropdown-trigger" @click.prevent>
              <a-avatar :size="32" :src="userProfilePicture || undefined">
                <template #icon v-if="!userProfilePicture">
                  <UserOutlined />
                </template>
              </a-avatar>
              <div class="user-dropdown-info">
                <span class="user-name">{{ userName }}</span>
                <span class="user-role">{{ userRole }}</span>
              </div>
              <DownOutlined class="dropdown-arrow" />
            </a>
            <template #overlay>
              <a-menu class="user-dropdown-menu">
                <a-menu-item key="profile" @click="goToProfile">
                  <UserOutlined />
                  <span>Profile</span>
                </a-menu-item>
                <a-menu-item key="settings" @click="goToSettings">
                  <SettingOutlined />
                  <span>Settings</span>
                </a-menu-item>
                <a-menu-divider />
                <a-menu-item key="logout" @click="handleLogout">
                  <LogoutOutlined style="color: #ef4444;" />
                  <span style="color: #ef4444;">Logout</span>
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>

          <!-- Sign In Button (when not logged in) -->
          <a-button v-else type="link" class="btn-sign-in" @click="handleSignIn">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM12 7C12 8.10457 11.1046 9 10 9C8.89543 9 8 8.10457 8 7C8 5.89543 8.89543 5 10 5C11.1046 5 12 5.89543 12 7ZM9.99993 11C7.98239 11 6.24394 12.195 5.45374 13.9157C6.55403 15.192 8.18265 16 9.99998 16C11.8173 16 13.4459 15.1921 14.5462 13.9158C13.756 12.195 12.0175 11 9.99993 11Z" fill="#111827"/>
            </svg>
            <span>Sign In</span>
          </a-button>
          <!-- / Header Control Buttons -->

          <!-- Header Search Input with Autocomplete -->
          <a-auto-complete
            v-model:value="searchValue"
            class="header-search-autocomplete"
            :options="searchSuggestions"
            @search="handleSearchInput"
            @select="handleSearchSelect"
            placeholder="Search pages, maintenance, reports..."
          >
            <template #option="{ value, label, path, icon, description }">
              <div class="search-suggestion-item">
                <span class="suggestion-icon">{{ icon }}</span>
                <div class="suggestion-content">
                  <div class="suggestion-label">{{ label }}</div>
                  <div class="suggestion-description">{{ description }}</div>
                </div>
              </div>
            </template>
            <a-input>
              <template #prefix>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M8 4C5.79086 4 4 5.79086 4 8C4 10.2091 5.79086 12 8 12C10.2091 12 12 10.2091 12 8C12 5.79086 10.2091 4 8 4ZM2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8C14 9.29583 13.5892 10.4957 12.8907 11.4765L17.7071 16.2929C18.0976 16.6834 18.0976 17.3166 17.7071 17.7071C17.3166 18.0976 16.6834 18.0976 16.2929 17.7071L11.4765 12.8907C10.4957 13.5892 9.29583 14 8 14C4.68629 14 2 11.3137 2 8Z" fill="#111827"/>
                </svg>
              </template>
            </a-input>
          </a-auto-complete>
          <!-- / Header Search Input -->

        </a-col>
        <!-- / Header Control Column -->

      </a-row>
    </a-layout-header>
    <!-- / Layout Header -->
  </component>
  <!-- / Main Header -->
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import { 
  UserOutlined, 
  DownOutlined, 
  SettingOutlined, 
  LogoutOutlined,
  BellOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined
} from '@ant-design/icons-vue'
import { authService } from '../../services/auth.service'
import { notificationsService } from '../../services/notifications.service'

export default {
  name: 'DashboardHeader',
  components: {
    UserOutlined,
    DownOutlined,
    SettingOutlined,
    LogoutOutlined,
    BellOutlined,
    InfoCircleOutlined,
    WarningOutlined,
    CloseCircleOutlined,
    CheckCircleOutlined,
  },
  props: {
    navbarFixed: {
      type: Boolean,
      default: false,
    },
    sidebarCollapsed: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const route = useRoute()
    const router = useRouter()
    const top = ref(0)
    const searchLoading = ref(false)
    const searchValue = ref('')
    const searchSuggestions = ref([])
    
    // Search index - pages and their related keywords
    const searchIndex = [
      { path: '/dashboard', label: 'Dashboard', icon: '', keywords: ['dashboard', 'home', 'overview', 'summary', 'statistics', 'stats'], description: 'Main dashboard overview' },
      { path: '/machines', label: 'Machines', icon: '', keywords: ['machines', 'devices', 'hvac', 'ac', 'air conditioning', 'sensor', 'esp32', 'equipment'], description: 'Monitor HVAC devices' },
      { path: '/location', label: 'Location Map', icon: '', keywords: ['location', 'map', 'floor', 'building', 'ecc', 'room', '501', '502'], description: 'Device location tracking' },
      { path: '/maintenance', label: 'Maintenance', icon: '', keywords: ['maintenance', 'schedule', 'task', 'repair', 'cleaning', 'filter', 'coil', 'compressor', 'inspection'], description: 'Maintenance scheduling' },
      { path: '/report', label: 'Reports', icon: '', keywords: ['report', 'analytics', 'analysis', 'chart', 'graph', 'performance', 'efficiency', 'ml', 'prediction'], description: 'Analytics and reports' },
      { path: '/notifications', label: 'Notifications', icon: '', keywords: ['notification', 'alert', 'warning', 'message', 'unread'], description: 'System notifications' },
      { path: '/history', label: 'Activity History', icon: '', keywords: ['history', 'activity', 'log', 'audit', 'action', 'login', 'logout'], description: 'Activity logs' },
      { path: '/settings', label: 'Settings', icon: '', keywords: ['settings', 'config', 'configuration', 'threshold', 'preferences'], description: 'System settings' },
      { path: '/profile', label: 'Profile', icon: '', keywords: ['profile', 'account', 'user', 'password', 'email'], description: 'User profile' },
    ]
    const wrapper = ref(null)

    const currentRouteName = computed(() => {
      return route.name || 'Dashboard'
    })

    // Auth state
    const isAuthenticated = ref(false)
    const userName = ref('User')
    const userRole = ref('User')
    const userProfilePicture = ref(null)

    // Notification state
    const notificationDropdownOpen = ref(false)
    const notifications = ref([])
    const unreadCount = ref(0)
    const loadingNotifications = ref(false)
    const markingAllRead = ref(false)

    // Load user data from localStorage
    const loadUserData = () => {
      isAuthenticated.value = authService.isAuthenticated()
      const user = authService.getUser()
      if (user) {
        userName.value = user.name || 'User'
        userRole.value = user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User'
        userProfilePicture.value = user.profile_picture || null
      }
    }

    // Load notifications from API
    const loadNotifications = async () => {
      if (!isAuthenticated.value) return
      loadingNotifications.value = true
      try {
        const data = await notificationsService.getAll({ limit: 5 })
        notifications.value = data.notifications || []
        await loadUnreadCount()
      } catch (error) {
        console.error('Failed to load notifications:', error)
      } finally {
        loadingNotifications.value = false
      }
    }

    // Load unread count
    const loadUnreadCount = async () => {
      try {
        const count = await notificationsService.getUnreadCount()
        unreadCount.value = count || 0
      } catch (error) {
        console.error('Failed to load unread count:', error)
      }
    }

    // Format time for display
    const formatTime = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins} min ago`
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
      return date.toLocaleDateString()
    }

    // Handle notification click
    const handleNotificationClick = async (item) => {
      if (!item.is_read) {
        try {
          await notificationsService.markAsRead(item.id)
          item.is_read = true
          unreadCount.value = Math.max(0, unreadCount.value - 1)
        } catch (error) {
          console.error('Failed to mark as read:', error)
        }
      }
    }

    // Mark all as read
    const markAllAsRead = async () => {
      markingAllRead.value = true
      try {
        await notificationsService.markAllAsRead()
        notifications.value.forEach(n => n.is_read = true)
        unreadCount.value = 0
        message.success('All notifications marked as read')
      } catch (error) {
        message.error('Failed to mark all as read')
      } finally {
        markingAllRead.value = false
      }
    }

    // Go to notifications page
    const goToNotifications = () => {
      notificationDropdownOpen.value = false
      router.push('/notifications')
    }

    // Watch dropdown open state to load notifications
    watch(notificationDropdownOpen, (isOpen) => {
      if (isOpen && isAuthenticated.value) {
        loadNotifications()
      }
    })

    const getContainer = () => {
      return wrapper.value || document.getElementById('layout-dashboard') || document.body
    }

    const resizeEventHandler = () => {
      top.value = top.value ? 0 : -0.01
    }

    const handleSearchInput = (value) => {
      if (!value || value.length < 1) {
        searchSuggestions.value = []
        return
      }
      
      const query = value.toLowerCase().trim()
      const results = []
      
      // Search through index using regex
      searchIndex.forEach(item => {
        const matchScore = calculateMatchScore(query, item)
        if (matchScore > 0) {
          results.push({
            value: item.path,
            label: item.label,
            path: item.path,
            icon: item.icon,
            description: item.description,
            score: matchScore
          })
        }
      })
      
      // Sort by score (higher = better match) and limit to 6 results
      results.sort((a, b) => b.score - a.score)
      searchSuggestions.value = results.slice(0, 6)
    }
    
    const calculateMatchScore = (query, item) => {
      let score = 0
      const queryWords = query.split(/\s+/)
      
      // Check label match (highest priority)
      if (item.label.toLowerCase().includes(query)) {
        score += 100
      }
      
      // Check keywords match
      queryWords.forEach(word => {
        if (word.length < 2) return
        item.keywords.forEach(keyword => {
          if (keyword.includes(word)) {
            score += 50
          }
          // Partial match with regex
          try {
            const regex = new RegExp(word, 'i')
            if (regex.test(keyword)) {
              score += 25
            }
          } catch (e) {
            // Invalid regex, skip
          }
        })
      })
      
      // Check description match
      if (item.description.toLowerCase().includes(query)) {
        score += 20
      }
      
      return score
    }
    
    const handleSearchSelect = (value) => {
      if (value) {
        router.push(value)
        searchValue.value = ''
        searchSuggestions.value = []
      }
    }
    
    const onSearch = (value) => {
      // Direct search - navigate to first result or show message
      if (searchSuggestions.value.length > 0) {
        router.push(searchSuggestions.value[0].path)
        searchValue.value = ''
        searchSuggestions.value = []
      } else if (value) {
        message.info('No matching pages found')
      }
    }

    const handleToggleSidebar = () => {
      emit('toggleSidebar', !props.sidebarCollapsed)
      resizeEventHandler()
    }

    const handleLogout = () => {
      authService.logout()
      isAuthenticated.value = false
      message.success('Logged out successfully')
      router.push('/login')
    }

    const goToProfile = () => {
      router.push('/profile')
    }

    const goToSettings = () => {
      router.push('/settings')
    }

    const handleSignIn = () => {
      router.push('/login')
    }

    onMounted(() => {
      wrapper.value = document.getElementById('layout-dashboard')
      window.addEventListener('resize', resizeEventHandler)
      loadUserData()
      if (authService.isAuthenticated()) {
        loadUnreadCount()
      }
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', resizeEventHandler)
    })

    return {
      top,
      searchLoading,
      searchValue,
      searchSuggestions,
      wrapper,
      currentRouteName,
      getContainer,
      handleSearchInput,
      handleSearchSelect,
      onSearch,
      handleToggleSidebar,
      resizeEventHandler,
      isAuthenticated,
      userName,
      userRole,
      userProfilePicture,
      handleLogout,
      goToProfile,
      goToSettings,
      handleSignIn,
      // Notification exports
      notificationDropdownOpen,
      notifications,
      unreadCount,
      loadingNotifications,
      markingAllRead,
      formatTime,
      handleNotificationClick,
      markAllAsRead,
      goToNotifications,
    }
  }
}
</script>

<style scoped>
.header-control {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}

.notification-bell {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.notification-bell:hover {
  background: #f3f4f6;
}

.btn-sign-in {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  transition: all 0.2s;
}

.btn-sign-in:hover {
  background: #f0f2f5;
}

.user-dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.user-dropdown-trigger:hover {
  background: #f3f4f6;
}

.user-dropdown-info {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.user-dropdown-info .user-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.user-dropdown-info .user-role {
  font-size: 12px;
  color: #6b7280;
}

.dropdown-arrow {
  font-size: 10px;
  color: #9ca3af;
}

.header-search {
  max-width: 300px;
}

/* Notification Dropdown Styles */
.notifications-dropdown-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  width: 360px;
  max-height: 480px;
  overflow: hidden;
}

.notifications-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.notifications-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.notifications-loading,
.notifications-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 12px;
  color: #9ca3af;
}

.notifications-list {
  max-height: 320px;
  overflow-y: auto;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;
}

.notification-item:hover {
  background: #f9fafb;
}

.notification-item.unread {
  background: #f0f9ff;
}

.notification-item.unread:hover {
  background: #e0f2fe;
}

.notification-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
}

.notification-icon.info {
  background: #dbeafe;
  color: #2563eb;
}

.notification-icon.warning {
  background: #fef3c7;
  color: #d97706;
}

.notification-icon.error {
  background: #fee2e2;
  color: #dc2626;
}

.notification-icon.success {
  background: #dcfce7;
  color: #16a34a;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notification-message {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-time {
  font-size: 12px;
  color: #9ca3af;
}

.notification-dot {
  width: 8px;
  height: 8px;
  background: #3b82f6;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 6px;
}

.header-notifications-dropdown .notifications-footer {
  border-top: 1px solid #f0f0f0;
  padding: 8px;
}

@media (max-width: 768px) {
  .header-control {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .header-search {
    width: 100%;
    max-width: 100%;
    order: 1;
  }
  
  .user-dropdown-info {
    display: none;
  }
}
</style>

<style>
/* Global styles for notification dropdown - Ant Design renders overlay in portal outside component */
.header-notifications-dropdown {
  z-index: 1050 !important;
}

.header-notifications-dropdown .ant-dropdown-menu {
  padding: 0;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.header-notifications-dropdown .notifications-dropdown-content {
  background: white;
  border-radius: 12px;
  width: 360px;
  max-height: 480px;
  overflow: hidden;
}

.header-notifications-dropdown .notifications-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.header-notifications-dropdown .notifications-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.header-notifications-dropdown .notifications-loading,
.header-notifications-dropdown .notifications-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 12px;
  color: #9ca3af;
}

.header-notifications-dropdown .notifications-list {
  max-height: 320px;
  overflow-y: auto;
}

.header-notifications-dropdown .notification-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;
}

.header-notifications-dropdown .notification-item:hover {
  background: #f9fafb;
}

.header-notifications-dropdown .notification-item.unread {
  background: #f0f9ff;
}

.header-notifications-dropdown .notification-item.unread:hover {
  background: #e0f2fe;
}

.header-notifications-dropdown .notification-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
}

.header-notifications-dropdown .notification-icon.info {
  background: #dbeafe;
  color: #2563eb;
}

.header-notifications-dropdown .notification-icon.warning {
  background: #fef3c7;
  color: #d97706;
}

.header-notifications-dropdown .notification-icon.error {
  background: #fee2e2;
  color: #dc2626;
}

.header-notifications-dropdown .notification-icon.success {
  background: #dcfce7;
  color: #16a34a;
}

.header-notifications-dropdown .notification-content {
  flex: 1;
  min-width: 0;
}

.header-notifications-dropdown .notification-title {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 2px;
}

.header-notifications-dropdown .notification-message {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.header-notifications-dropdown .notification-time {
  font-size: 12px;
  color: #9ca3af;
}

.header-notifications-dropdown .notification-dot {
  width: 8px;
  height: 8px;
  background: #3b82f6;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 6px;
}

.header-notifications-dropdown .notifications-footer {
  border-top: 1px solid #f0f0f0;
  padding: 8px;
}

@media (max-width: 768px) {
  .header-notifications-dropdown .notifications-dropdown-content {
    width: 300px;
  }
}

/* Search Autocomplete Styles */
.header-search-autocomplete {
  width: 280px;
}

.header-search-autocomplete .ant-input-affix-wrapper {
  border-radius: 8px;
}

.search-suggestion-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 4px;
}

.search-suggestion-item .suggestion-icon {
  font-size: 20px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border-radius: 8px;
}

.search-suggestion-item .suggestion-content {
  flex: 1;
}

.search-suggestion-item .suggestion-label {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
}

.search-suggestion-item .suggestion-description {
  font-size: 12px;
  color: #6b7280;
}
</style>